import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Search, Users2, Save, X, Check } from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";

// V1.36 · Panel Proveedores — CRUD sencillo.
export default function SuppliersPanel({ sectionId }) {
  const pin = usePinSecurity();
  const [items, setItems] = useState([]);
  const [tree, setTree] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // proveedor en edición (o {} para nuevo)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q };
      if (sectionId) params.section_id = sectionId;
      const [{ data }, { data: accs }] = await Promise.all([
        api.get("/accounting/suppliers", { params }),
        api.get("/accounting/accounts"),
      ]);
      setItems(data?.items || []);
      setTree(accs?.tree || {});
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  }, [q, sectionId]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try {
      await pin.require("accounting.supplier.delete");
      await api.delete(`/accounting/suppliers/${id}`);
      toast.success("Proveedor eliminado");
      load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    }
  };

  return (
    <div className="space-y-3" data-testid="suppliers-panel">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o CIF…"
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-neutral-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            data-testid="suppliers-search"
          />
        </div>
        <button
          onClick={() => setEditing({})}
          className="btn-primary !h-10 !px-4 !text-sm"
          data-testid="suppliers-new-btn"
        >
          <Plus className="h-4 w-4" /> Nuevo
        </button>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-center text-sm text-neutral-500">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Cargando…
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-sm text-neutral-500">
            <Users2 className="mx-auto h-6 w-6 mb-2 text-neutral-300" />
            No hay proveedores registrados
          </div>
        ) : (
          <table className="w-full text-sm table-fixed">
            <colgroup>
              <col />
              <col style={{ width: "140px" }} />
              <col style={{ width: "280px" }} />
              <col style={{ width: "80px" }} />
              <col style={{ width: "130px" }} />
            </colgroup>
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-4 py-3">Nombre</th>
                <th className="text-left px-4 py-3">CIF</th>
                <th className="text-left px-4 py-3">Cuenta / Tipo</th>
                <th className="text-right px-4 py-3">Uso</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-t border-neutral-100 hover:bg-neutral-50/60 h-[56px]"
                    data-testid={`supplier-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium align-middle truncate" title={s.name}>{s.name}</td>
                  <td className="px-4 py-3 text-neutral-600 align-middle truncate">{s.cif || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600 align-middle truncate" title={s.main_account ? `${s.main_account} · ${s.expense_kind}` : ""}>
                    {s.main_account
                      ? <><b className="text-neutral-800">{s.main_account}</b> · {s.expense_kind}</>
                      : <span className="text-neutral-400 italic">Sin clasificar</span>}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums align-middle">{s.usage_count || 0}</td>
                  <td className="px-4 py-3 text-right space-x-1 align-middle whitespace-nowrap">
                    <button onClick={() => setEditing(s)}
                            className="text-xs px-2 py-1 rounded hover:bg-neutral-100"
                            data-testid={`supplier-edit-${s.id}`}>
                      Editar
                    </button>
                    <button onClick={() => del(s.id)}
                            className="text-xs px-1.5 py-1 rounded text-red-600 hover:bg-red-50"
                            title="Eliminar"
                            data-testid={`supplier-del-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing !== null && (
        <SupplierEditor
          initial={editing}
          tree={tree}
          sectionId={sectionId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SupplierEditor({ initial, tree, sectionId, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "",
    cif: initial?.cif || "",
    main_account: initial?.main_account || "",
    expense_kind: initial?.expense_kind || "",
    active: initial?.active !== false,
    notes: initial?.notes || "",
    section_id: initial?.section_id || sectionId || "",
  });
  const [saving, setSaving] = useState(false);

  const kinds = useMemo(() => {
    const arr = tree[form.main_account] || [];
    return arr.filter((a) => a.active).map((a) => a.kind);
  }, [tree, form.main_account]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nombre obligatorio");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/accounting/suppliers", form);
        toast.success("Proveedor creado");
      } else {
        await api.patch(`/accounting/suppliers/${initial.id}`, form);
        toast.success("Proveedor actualizado");
      }
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => e.target === e.currentTarget && onClose?.()}
         data-testid="supplier-editor">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <header className="p-4 border-b border-neutral-100 flex items-center justify-between">
          <div className="font-bold">{isNew ? "Nuevo proveedor" : "Editar proveedor"}</div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-neutral-100">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <Field label="Nombre">
            <input value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   className={fieldCls}
                   data-testid="supplier-name" />
          </Field>
          <Field label="CIF">
            <input value={form.cif}
                   onChange={(e) => setForm({ ...form, cif: e.target.value.toUpperCase() })}
                   className={fieldCls}
                   data-testid="supplier-cif" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Cuenta principal">
              <select value={form.main_account}
                      onChange={(e) => setForm({ ...form, main_account: e.target.value, expense_kind: "" })}
                      className={fieldCls}
                      data-testid="supplier-main-account">
                <option value="">—</option>
                {Object.keys(tree).sort().map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </Field>
            <Field label="Tipo de gasto">
              <select value={form.expense_kind}
                      onChange={(e) => setForm({ ...form, expense_kind: e.target.value })}
                      className={fieldCls}
                      disabled={!form.main_account}
                      data-testid="supplier-expense-kind">
                <option value="">—</option>
                {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notas">
            <input value={form.notes}
                   onChange={(e) => setForm({ ...form, notes: e.target.value })}
                   className={fieldCls}
                   data-testid="supplier-notes" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active}
                   onChange={(e) => setForm({ ...form, active: e.target.checked })}
                   data-testid="supplier-active" />
            Activo
          </label>
        </div>
        <footer className="p-4 border-t border-neutral-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary !h-10 !px-4">Cancelar</button>
          <button onClick={save} disabled={saving}
                  className="btn-primary !h-10 !px-5"
                  data-testid="supplier-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar
          </button>
        </footer>
      </div>
    </div>
  );
}

const fieldCls =
  "w-full h-10 rounded-lg border border-neutral-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-neutral-50";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

// exportado para eslint (usado en checkboxes futuros)
void Check;
